import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { FineDetailsPublicResponse, PaymentSetupPublicResponse } from "api"
import Image from "next/image"
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { ErrorType } from "../Payment"
import Success from "./Success"
const API_URL = process.env.API_URL

const OuterLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    padding-bottom: 48px;
  }
`

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 32px;
  }
`

const StyledIframe = styled.iframe`
  border: none;
  height: 1310px;
  max-width: 990px;
  width: 100%;
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
    flex-direction: row;
  }
`

const TextDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

export default function ThirdStage({
  data,
  paymentData,
  stepData,
  setError,
  titleRef,
}: {
  data: FineDetailsPublicResponse
  paymentData: PaymentSetupPublicResponse
  stepData: {
    icon: string
    title: string
    subTitle: string
  }
  setError: Dispatch<SetStateAction<ErrorType | undefined>>
  titleRef: RefObject<HTMLHeadingElement | null>
}) {
  const [iframeData, setIframeData] = useState<{
    confirmationNumber: string
    email: string
    firstname: string
    lastname: string
    cardholderid: string
    pdf: string
    phone: string
  } | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Focus management - move focus to title when component mounts
  useEffect(() => {
    if (titleRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        titleRef.current?.focus()
      }, 100)
    }
  }, [titleRef])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== API_URL) {
        return
      }
      const res = event.data as {
        success: string
        confirmationNumber: string
        email: string
        firstname: string
        lastname: string
        cardholderid: string
        pdf: string
        phone: string
      }
      if (res.success !== "True") {
        setError("iframeError")
        return
      }

      const decodedFirstname = document.createElement("textarea")
      decodedFirstname.innerHTML = res.firstname
      const decodedLastname = document.createElement("textarea")
      decodedLastname.innerHTML = res.lastname
      const decodedPdf = document.createElement("textarea")
      decodedPdf.innerHTML = res.pdf

      setIframeData({
        confirmationNumber: res.confirmationNumber,
        email: res.email,
        firstname: decodedFirstname.value,
        lastname: decodedLastname.value,
        cardholderid: res.cardholderid,
        pdf: decodedPdf.value,
        phone: res.phone,
      })
    }

    window.addEventListener("message", handleMessage)

    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [])
  return (
    <OuterLayout>
      {iframeData ? (
        <Success data={data} iframeData={iframeData} />
      ) : (
        <>
          <TextBox>
            <Image
              src={getUmbracoMediaUrl(stepData?.icon)}
              width={80}
              height={80}
              alt="icon"
            />
            <TextDiv>
              <Typography variant="h3" ref={titleRef} tabIndex={-1}>
                {stepData?.title}
              </Typography>
              <Typography variant="body1">{stepData?.subTitle}</Typography>
            </TextDiv>
          </TextBox>
          <StyledLayout>
            <StyledIframe src={paymentData.mpiHostedPageUrl} ref={iframeRef} />
          </StyledLayout>
        </>
      )}
    </OuterLayout>
  )
}
